import { useMemo } from 'react';
import { PrimaryTable } from '@Calix-Commerce/design-system/components';
import { theme } from '@Calix-Commerce/design-system/globals';
import { ExtendedWarrantyLine } from '../ExtendedWarrantyLine';
import { ExtendedWarrantyLineConfig, ExtendedWarrantyOutput } from '../../types';
import { useFormatPrice } from '../../hooks/useFormatPrice';
import { PrimaryTableRow } from '../styledComponents';
import { ExtendedWarrantySelectAll } from '../ExtendedWarrantySelectAll';

export type ExtendedWarrantyProps = {
  currency: string;
  configs: ExtendedWarrantyLineConfig[];
  configState: ExtendedWarrantyOutput;
  quoteTotal: number;
  readOnly: boolean;
  showSelectAll: boolean;
  onChange: (newValue: ExtendedWarrantyOutput) => void;
};

export const ExtendedWarranty = ({
  currency,
  configs,
  configState,
  quoteTotal,
  readOnly,
  showSelectAll = false,
  onChange,
}: ExtendedWarrantyProps) => {
  const formatPrice = useFormatPrice(currency);

  const extendedWarrantyTotal = useMemo(
    () => Object.values(configState).reduce((total, config) => total + config.warrantyPrice, 0),
    [configState]
  );

  const extendedQuoteTotal = useMemo(
    () => quoteTotal + extendedWarrantyTotal,
    [quoteTotal, extendedWarrantyTotal]
  );

  return (
    <PrimaryTable>
      <thead>
        <tr>
          <th>&nbsp;</th>
          <th>Part Number</th>
          <th>Part Name</th>
          <th>Qty</th>
          <th>Standard Warranty Duration</th>
          <th>Extended Warranty Duration</th>
          <th>Extended Warranty Price</th>
        </tr>
      </thead>
      <tbody>
        {showSelectAll && (
          <ExtendedWarrantySelectAll
            configs={configs}
            configState={configState}
            readOnly={readOnly}
            onChange={onChange}
          />
        )}
        {configs.map((config, index) => (
          <ExtendedWarrantyLine
            key={config.warrantedPartNumber}
            row={index + 1}
            config={config}
            configState={configState[config.warrantedPartNumber]}
            currency={currency}
            readOnly={readOnly}
            onChange={onChange}
          />
        ))}
        <PrimaryTableRow darkBackground={true}>
          <td style={{ textAlign: 'right' }} colSpan={6}>
            Extended Warranty Total
          </td>
          <td>{formatPrice(extendedWarrantyTotal)}</td>
        </PrimaryTableRow>
        <PrimaryTableRow
          style={{ backgroundColor: theme?.colors?.primary, color: theme?.colors?.textLight }}
        >
          <td style={{ textAlign: 'right' }} colSpan={6}>
            Quote Total
          </td>
          <td>{formatPrice(extendedQuoteTotal)}</td>
        </PrimaryTableRow>
      </tbody>
    </PrimaryTable>
  );
};
